import { convexQuery, useConvexAuth } from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { AUTH_EVENTS } from "@/constants/analyticsEvents";
import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/ui/logo";
import { api } from "@cvx/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useAuthActions } from "@convex-dev/auth/react";
import { useForm } from "@tanstack/react-form";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-form-adapter";

export const Route = createFileRoute("/_app/login/_layout/")({
  component: Login,
});

function Login() {
  const [step, setStep] = useState<
    | "signIn"
    | "signUp"
    | "reset"
    | { email: string; type: "verification" }
    | { email: string; type: "reset" }
  >("signUp");
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const navigate = useNavigate();

  useEffect(() => {
    if ((isLoading && !isAuthenticated) || !user) {
      return;
    }

    if (!isLoading && isAuthenticated) {
      navigate({ to: DashboardRoute.fullPath });
    }
  }, [user, isLoading, isAuthenticated]);

  if (step === "signIn" || step === "signUp") {
    return (
      <LoginForm
        step={step}
        onToggleStep={setStep}
        onVerification={(email) => {
          setStep({ email, type: "verification" });
        }}
        onForgotPassword={() => setStep("reset")}
      />
    );
  }

  if (step === "reset") {
    return (
      <ForgotPasswordForm
        onResetRequest={(email) => setStep({ email, type: "reset" })}
        onBack={() => setStep("signIn")}
      />
    );
  }

  if (typeof step === "object" && step.type === "verification") {
    return <VerifyForm email={step.email} onBack={() => setStep("signIn")} />;
  }

  if (typeof step === "object" && step.type === "reset") {
    return (
      <ResetPasswordForm email={step.email} onBack={() => setStep("signIn")} />
    );
  }

  return null;
}

function LoginForm({
  step,
  onToggleStep,
  onVerification,
  onForgotPassword,
}: {
  step: "signIn" | "signUp";
  onToggleStep: (step: "signIn" | "signUp") => void;
  onVerification: (email: string) => void;
  onForgotPassword: () => void;
}) {
  const { signIn } = useAuthActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { track } = useMixpanel();

  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);

      try {
        console.log("🚀 Starting authentication with:", {
          step,
          email: value.email,
        });

        const result = await signIn("password", {
          ...value,
          flow: step,
        });

        console.log("✅ Sign-in result:", {
          result,
          step,
          resultType: typeof result,
        });

        // For sign-up, if verification is configured, we need to show verification form
        if (step === "signUp") {
          console.log("📝 Processing sign-up result:", result);
          // If result.signingIn is false, email verification is required
          if (
            result &&
            typeof result === "object" &&
            "signingIn" in result &&
            !result.signingIn
          ) {
            console.log(
              "📧 Verification required - redirecting to verification form",
            );

            // Track verification code sent
            track(AUTH_EVENTS.VERIFICATION_CODE_SENT, {
              email: value.email,
              flow: "signup",
              timestamp: new Date().toISOString(),
            });

            onVerification(value.email);
            toast({
              title: "Verification required",
              description: "Please check your email for a verification code.",
            });
            return;
          } else {
            // Sign-up was successful without verification needed
            console.log("🎉 Sign-up successful without verification needed");

            // Track successful signup
            track(AUTH_EVENTS.USER_SIGNED_UP, {
              email: value.email,
              verification_required: false,
              timestamp: new Date().toISOString(),
            });

            toast({
              title: "Account created!",
              description:
                "Welcome! Your account has been created successfully.",
            });
          }
        } else if (step === "signIn") {
          console.log("🔑 Processing sign-in result:", result);
          // For sign-in, if result.signingIn is false, might need verification
          if (
            result &&
            typeof result === "object" &&
            "signingIn" in result &&
            !result.signingIn
          ) {
            console.log("📧 Sign-in verification required");

            // Track verification code sent
            track(AUTH_EVENTS.VERIFICATION_CODE_SENT, {
              email: value.email,
              flow: "signin",
              timestamp: new Date().toISOString(),
            });

            onVerification(value.email);
            toast({
              title: "Verification required",
              description: "Please check your email for a verification code.",
            });
            return;
          } else {
            console.log("🎉 Sign-in successful");

            // Track successful signin
            track(AUTH_EVENTS.USER_SIGNED_IN, {
              email: value.email,
              timestamp: new Date().toISOString(),
            });

            toast({
              title: "Success",
              description: "Welcome back! You've been signed in.",
            });
          }
        }
      } catch (error) {
        console.error("❌ Authentication error:", error);
        toast({
          variant: "destructive",
          title: "Authentication failed",
          description:
            error instanceof ConvexError
              ? error.data
              : "An unexpected error occurred. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="mb-2">
          <Logo width={64} height={64} />
        </div>
        <h3 className="text-center text-2xl font-medium text-primary">
          {step === "signIn" ? "Welcome back!" : "Create your account"}
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          {step === "signIn"
            ? "Sign in to continue to CreatorGrid"
            : "Get started with CreatorGrid today"}
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <form.Field
              name="email"
              validators={{
                onSubmit: z
                  .string()
                  .max(256)
                  .email("Email address is not valid."),
              }}
              children={(field) => (
                <Input
                  placeholder="Email"
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.email?.errors.length > 0 && (
              <span className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.email?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <form.Field
              name="password"
              validators={{
                onSubmit: z
                  .string()
                  .min(8, "Password must be at least 8 characters."),
              }}
              children={(field) => (
                <Input
                  placeholder="Password"
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.password?.errors.length > 0 && (
              <span className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.password?.errors.join(" ")}
              </span>
            )}
          </div>
        </div>

        {step === "signIn" && (
          <div className="flex w-full justify-end">
            <Button
              type="button"
              variant="ghost"
              className="h-auto p-0 text-sm text-primary/60 hover:text-primary hover:bg-transparent"
              onClick={onForgotPassword}
            >
              Forgot password?
            </Button>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : step === "signIn" ? (
            "Sign In"
          ) : (
            "Create Account"
          )}
        </Button>
      </form>

      <Button
        variant="ghost"
        className="w-full"
        onClick={() => onToggleStep(step === "signIn" ? "signUp" : "signIn")}
      >
        {step === "signIn"
          ? "Don't have an account? Sign up"
          : "Already have an account? Sign in"}
      </Button>

      <div className="relative flex w-full items-center justify-center">
        <span className="absolute w-full border-b border-border" />
        <span className="z-10 bg-card px-2 text-xs font-medium uppercase text-primary/60">
          Or continue with
        </span>
      </div>

      <div className="w-full">
        <Button
          variant="outline"
          className="w-full gap-2 bg-transparent"
          onClick={async () => {
            try {
              await signIn("google", { redirectTo: "/dashboard" });
            } catch (error) {
              toast({
                variant: "destructive",
                title: "Google sign-in failed",
                description:
                  error instanceof ConvexError
                    ? error.data
                    : "Unable to sign in with Google. Please try again.",
              });
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 24 24"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Google
        </Button>
      </div>

      <p className="px-12 text-center text-sm font-normal leading-normal text-primary/60">
        By clicking continue, you agree to our{" "}
        <a
          className="underline hover:text-primary"
          href="/terms"
          target="_blank"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          className="underline hover:text-primary"
          href="/privacy"
          target="_blank"
        >
          Privacy Policy.
        </a>
      </p>
    </div>
  );
}

function ForgotPasswordForm({
  onResetRequest,
  onBack,
}: {
  onResetRequest: (email: string) => void;
  onBack: () => void;
}) {
  const { signIn } = useAuthActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);

      try {
        await signIn("password", {
          email: value.email,
          flow: "reset",
        });

        onResetRequest(value.email);
        toast({
          title: "Reset code sent",
          description: "Please check your email for a password reset code.",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Failed to send reset code",
          description:
            error instanceof ConvexError
              ? error.data
              : "Unable to send password reset code. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="mb-2">
          <Logo width={64} height={64} />
        </div>
        <h3 className="text-center text-2xl font-medium text-primary">
          Reset your password
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Enter your email address and we'll send you a code to reset your
          password.
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <form.Field
            name="email"
            validators={{
              onSubmit: z
                .string()
                .max(256)
                .email("Email address is not valid."),
            }}
            children={(field) => (
              <Input
                placeholder="Email"
                type="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  "border-destructive focus-visible:ring-destructive"
                }`}
              />
            )}
          />
          {form.state.fieldMeta.email?.errors.length > 0 && (
            <span className="text-sm text-destructive dark:text-destructive-foreground">
              {form.state.fieldMeta.email?.errors.join(" ")}
            </span>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            "Send Reset Code"
          )}
        </Button>
      </form>

      <Button variant="ghost" className="w-full" onClick={onBack}>
        Back to Sign In
      </Button>
    </div>
  );
}

function ResetPasswordForm({
  email,
  onBack,
}: {
  email: string;
  onBack: () => void;
}) {
  const { signIn } = useAuthActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
    onSubmit: async ({ value }) => {
      if (value.newPassword !== value.confirmPassword) {
        toast({
          variant: "destructive",
          title: "Passwords don't match",
          description: "Please make sure both password fields match.",
        });
        return;
      }

      setIsSubmitting(true);

      try {
        await signIn("password", {
          email,
          code: value.code,
          newPassword: value.newPassword,
          flow: "reset-verification",
        });

        toast({
          title: "Password reset successful",
          description: "Your password has been reset. You've been signed in.",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Password reset failed",
          description:
            error instanceof ConvexError
              ? error.data
              : "Invalid code or unable to reset password. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="mb-2">
          <Logo width={64} height={64} />
        </div>
        <h3 className="text-center text-2xl font-medium text-primary">
          Enter new password
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Enter the code from your email and your new password.
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="code" className="sr-only">
              Reset Code
            </label>
            <form.Field
              name="code"
              validators={{
                onSubmit: z
                  .string()
                  .min(8, "Code must be at least 8 characters."),
              }}
              children={(field) => (
                <Input
                  placeholder="Reset Code"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.code?.errors.length > 0 && (
              <span className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.code?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="newPassword" className="sr-only">
              New Password
            </label>
            <form.Field
              name="newPassword"
              validators={{
                onSubmit: z
                  .string()
                  .min(8, "Password must be at least 8 characters."),
              }}
              children={(field) => (
                <Input
                  placeholder="New Password"
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.newPassword?.errors.length > 0 && (
              <span className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.newPassword?.errors.join(" ")}
              </span>
            )}
          </div>

          <div className="flex w-full flex-col gap-1.5">
            <label htmlFor="confirmPassword" className="sr-only">
              Confirm New Password
            </label>
            <form.Field
              name="confirmPassword"
              validators={{
                onSubmit: z
                  .string()
                  .min(8, "Password must be at least 8 characters."),
              }}
              children={(field) => (
                <Input
                  placeholder="Confirm New Password"
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className={`bg-transparent ${
                    field.state.meta?.errors.length > 0 &&
                    "border-destructive focus-visible:ring-destructive"
                  }`}
                />
              )}
            />
            {form.state.fieldMeta.confirmPassword?.errors.length > 0 && (
              <span className="text-sm text-destructive dark:text-destructive-foreground">
                {form.state.fieldMeta.confirmPassword?.errors.join(" ")}
              </span>
            )}
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            "Reset Password"
          )}
        </Button>
      </form>

      <Button variant="ghost" className="w-full" onClick={onBack}>
        Back to Sign In
      </Button>
    </div>
  );
}

function VerifyForm({ email, onBack }: { email: string; onBack: () => void }) {
  const { signIn } = useAuthActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const navigate = useNavigate();
  const {
    setContractId,
    setAppId,
    setCreatorId,
    pendingCampaignId,
    clearPendingCampaignId,
  } = useAccount();

  // Check if user's email is already verified and redirect accordingly
  useEffect(() => {
    if (!user || !user.emailVerificationTime) {
      return;
    }

    navigate({ to: DashboardRoute.fullPath });
  }, [
    user,
    navigate,
    setAppId,
    setContractId,
    setCreatorId,
    pendingCampaignId,
    clearPendingCampaignId,
  ]);

  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      code: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);

      try {
        await signIn("password", {
          email,
          code: value.code,
          flow: "email-verification",
        });

        toast({
          title: "Email verified!",
          description: "Your account has been successfully verified.",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Verification failed",
          description:
            error instanceof ConvexError
              ? error.data
              : "Invalid verification code. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResendCode = async () => {
    try {
      await signIn("password", { email, flow: "signUp" });
      toast({
        title: "Code sent",
        description: "A new verification code has been sent to your email.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to send code",
        description:
          error instanceof ConvexError
            ? error.data
            : "Unable to send verification code. Please try again.",
      });
    }
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="mb-2">
          <Logo width={64} height={64} />
        </div>
        <h3 className="text-center text-2xl font-medium text-primary">
          Check your inbox!
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          We've just emailed you a verification code. Please enter it below to
          complete your account setup.
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor="code" className="sr-only">
            Verification Code
          </label>
          <form.Field
            name="code"
            validators={{
              onSubmit: z
                .string()
                .min(8, "Code must be at least 8 characters."),
            }}
            children={(field) => (
              <Input
                placeholder="Verification Code"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  "border-destructive focus-visible:ring-destructive"
                }`}
              />
            )}
          />
          {form.state.fieldMeta.code?.errors.length > 0 && (
            <span className="text-sm text-destructive dark:text-destructive-foreground">
              {form.state.fieldMeta.code?.errors.join(" ")}
            </span>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : "Verify Email"}
        </Button>
      </form>

      <div className="flex w-full flex-col gap-2">
        <p className="text-center text-sm font-normal text-primary/60">
          Didn't receive the code?
        </p>
        <Button
          onClick={handleResendCode}
          variant="ghost"
          className="w-full hover:bg-transparent"
        >
          Resend Code
        </Button>
        <Button
          onClick={onBack}
          variant="ghost"
          className="w-full hover:bg-transparent"
        >
          Back to Sign In
        </Button>
      </div>
    </div>
  );
}
